import React, { FC, useState } from 'react';
import { Machine, MachineEventSearchItem } from '@interfaces/machines';
import { ProductionBatchStatistics } from '@interfaces/search';
import { StyleSheet, Text, View } from 'react-native';
import i18n from 'i18n-js';
import { scale } from '@components/frame';
import { isPhone, isWeb } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { modalBg, modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { ProductivityStatus } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import ActiveEventTimes from './components/ActiveEventTimes';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';
import BlockWrapper from '@components/common/BlockWrapper';

export interface ProductivityStatusInfoProps {
  selectedItem: Machine;
  batch: ProductionBatchStatistics;
  C: ColorFunction;
  offset: number;
  events?: MachineEventSearchItem[];
  actualTimeForLatestEvent?: number;
  hide?: () => void;
}

const ProductivityStatusInfo: FC<ProductivityStatusInfoProps> = ({ selectedItem, batch, C, offset, events, ...rest }) => {
  const [actualTimeForLatestEvent, setActualTimeForLatestEvent] = useState<number>(0);

  return (
    <View
      style={[
        { padding: scale(15), paddingBottom: scale(20), backgroundColor: C(modalBg) },
        isPhone && { flex: 1 },
        !isPhone && { paddingTop: scale(22) },
      ]}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: isPhone ? 'flex-start' : 'center',
          marginBottom: scale(27),
        }}
      >
        <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{i18n.t('Productivity Status')}</Text>
        <TouchableElement onPress={rest?.hide}>
          {() => (
            <Text
              style={[
                font.Headline,
                { color: C('Link/Light/Primary'), position: 'absolute', top: scale(0), right: scale(0) },
                isWeb && { cursor: 'pointer' },
              ]}
            >
              {i18n.t('Done')}
            </Text>
          )}
        </TouchableElement>
      </View>

      <BlockWrapper marginBottom={scale(28)}>
        <InputLine
          {...{
            borderBottomRadius: false,
            borderTopRadius: true,
            data: [],
            onlyLabel: false,
            index: 0,
            i: {
              name: 'Machine Status',
              isText: true,
              value: selectedItem?.machineStatus?.subStatus,
            },
            C,
            loading: false,
            layoutForPhone: true,
            withoutStretchingForContainer: true,
            bg: modalFieldBg,
          }}
        />

        <InputLine
          {...{
            borderBottomRadius: false,
            borderTopRadius: false,
            data: [],
            onlyLabel: false,
            index: 0,
            i: {
              name: 'Execution Mode',
              isText: true,
              value: selectedItem?.machineStatus?.executionMode,
            },
            C,
            loading: false,
            layoutForPhone: true,
            withoutStretchingForContainer: true,
            bg: modalFieldBg,
          }}
        />

        <InputLine
          {...{
            borderBottomRadius: true,
            borderTopRadius: false,
            data: [{}],
            onlyLabel: false,
            index: 0,
            i: {
              name: 'Ready',
              ValueComponent: <ReadyPcsQuantity C={C} batch={batch} />,
            },
            C,
            loading: false,
            layoutForPhone: true,
            withoutStretchingForContainer: true,
            bg: modalFieldBg,
          }}
        />
      </BlockWrapper>

      <BlockWrapper title="INTERPRETED AS" marginBottom={scale(28)}>
        <ProductivityStatus
          {...{
            setActualTimeForLatestEvent,
            bg: modalFieldBg,
            batch,
            C,
            machineStatus: selectedItem.machineStatus,
            offset,
            menuItemTextStyles: [font.Body, { color: C('Label/Light/Primary') }],
            borderRadiusConfig: styles.borderRadiusConfig,
            withoutStretchingForContainer: true,
            separateTextAndIconForMobile: true,
            layoutForPhone: true,
            rightColumnStyles: {},
            leftColumnStyles: {
              alignItems: 'center',
              flexDirection: 'row',
              justifyContent: 'flex-start',
            },
          }}
        />
      </BlockWrapper>

      <ActiveEventTimes {...{ C, batch, offset, eventDetails: events?.at(-1), actualTimeForLatestEvent }} />
    </View>
  );
};

const styles = StyleSheet.create({
  borderRadiusConfig: { borderRadius: scale(SCREEN_BORDER_RADIUS) },
});

export default ProductivityStatusInfo;
